import { useCallback, useMemo } from 'react';

import { useTranslate } from '@dreryk/react-common';

import { patientTranslationKeys } from '../../../../translations';
import { FormSectionHeader } from '../../../FormSectionHeader';
import { FirstNameField } from '../../../PersonForm/FirstNameField';
import {
   createGuardianExternalIdentifierTypeVisitor,
   GuardianExternalIdentifierTypeField,
   GuardianExternalIdentifierTypeVisitor,
} from '../../../PersonForm/GuardianExternalIdentifier/GuardianExternalIdentifierTypeField';
import { GuardianExternalIdentifierValueField } from '../../../PersonForm/GuardianExternalIdentifier/GuardianExternalIdentifierValueField';
import { NationalIdentificationNumberCountryField } from '../../../PersonForm/GuardianExternalIdentifier/NationalIdentificationNumberCountryField';
import { PassportCountryField } from '../../../PersonForm/GuardianExternalIdentifier/PassportCountryField';
import { PassportValidToField } from '../../../PersonForm/GuardianExternalIdentifier/PassportValidToField';
import { LastNameField } from '../../../PersonForm/LastNameField';
import { MiddleNameField } from '../../../PersonForm/MiddleNameField';
import { StyledFormSection } from '../../../StyledFormSection';
import { GuardianDetailsFormController } from '../GuardianDetailsModal';

type PersonnelDataFormSectionProps = Pick<
   GuardianDetailsFormController,
   'register' | 'setValue' | 'setFocus' | 'watch'
> & {
   readonly statutoryRepresentative: boolean;
};

export const PersonalDataSection = (props: PersonnelDataFormSectionProps): JSX.Element => {
   const { register, setFocus, setValue, watch } = props;
   const translate = useTranslate();

   const { guardianExternalIdentifierType } = watch(['guardianExternalIdentifierType']);

   const guardianExternalIdentifierTypeVisitor = useMemo<GuardianExternalIdentifierTypeVisitor>(
      () => createGuardianExternalIdentifierTypeVisitor(guardianExternalIdentifierType),
      [guardianExternalIdentifierType]
   );

   const handleGuardianExternalIdentifierType = useCallback(() => {
      setValue('guardianExternalIdentifierValue', null);
      setFocus('guardianExternalIdentifierValue');
   }, [setValue, setFocus]);

   return (
      <div>
         <FormSectionHeader>{translate(patientTranslationKeys.basicData)}</FormSectionHeader>
         <StyledFormSection testId={'personalData'}>
            <FirstNameField register={register} />
            <MiddleNameField register={register} />
            <LastNameField register={register} />
            <GuardianExternalIdentifierTypeField
               name={'guardianExternalIdentifierType'}
               register={register}
               onSelect={handleGuardianExternalIdentifierType}
               statutoryRepresentative={props.statutoryRepresentative}
            />
            <GuardianExternalIdentifierValueField
               name={'guardianExternalIdentifierValue'}
               register={register}
               guardianExternalIdentifierTypeVisitor={guardianExternalIdentifierTypeVisitor}
            />
            {guardianExternalIdentifierTypeVisitor({
               visitPesel: () => null,

               visitPassport: () => (
                  <>
                     <div />
                     <PassportCountryField name={'passportCountry'} register={register} />
                     <PassportValidToField name={'passportValidTo'} register={register} />
                  </>
               ),

               visitNationalIdentificationNumber: () => (
                  <>
                     <NationalIdentificationNumberCountryField
                        name={'nationalIdentificationNumberCountry'}
                        register={register}
                     />
                  </>
               ),
            })}
         </StyledFormSection>
      </div>
   );
};
